import { Dropdown, FileUpload, TextInput } from "../../../../components/ui/inputs";
import localization from "../../../../config/localization";
import { Button } from "../../../../components/ui/Button";
import { useEffect, useState } from "react";
import { RetrieveBankDetailResponse } from "../../models/response";
import verificationService from "../../service/verification.service";
import { useLanguage } from "../../../../config/localization/LanguageProvider";
import { SupportedLanguages } from "../../../../models/constants/supportedLanguages";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import { UserBankAccountDetail } from "../../models/response/retrieveUserBankAccountResponse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { BlobContainerConsts } from "../../../../models/constants/blobContainerConsts";
import { BLOB_IMAGE_URL } from "../../../../config/appConfig";
import userDeviceStore from "../../../../store/device.store";
import { DeviceType } from "../../../../models/constants/deviceType";
import { Header4 } from "../../../../components/ui/Headers";
import { Card, CardBody, CardHeader } from "../../../../components/ui/cards";
import { userPendingStore } from "../../../users/store/userPendingData.store";
import { BankAccountStatus } from "../../../../models/enums/bankAccountStatus";
import { increaseInboxUnreadCount } from "../../../users/reducers/userProfileReducer";
import { useAppDispatch } from "../../../../store/hooks";
import { Validations } from "../../../../models/constants/validations";

export function BankingForm({
  bankAccount,
  handleBackButton,
}: {
  bankAccount?: UserBankAccountDetail | null;
  handleBackButton: () => void;
}) {
  const { language } = useLanguage();
  const dispatch = useAppDispatch();
  const [bankList, setBankList] = useState<RetrieveBankDetailResponse>();

  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [beneficiaryName, setBeneficiaryName] = useState<string | null>(null);
  const [accountNumber, setAccountNumber] = useState<string | null>(null);
  const [branchName, setBranchName] = useState<string | null>(null);
  const [ifsc, setIfsc] = useState<string | null>(null);

  const [beneficiaryNameError, setBeneficiaryNameError] = useState<
    string | null
  >(null);
  const [accountNumberError, setAccountNumberError] = useState<string | null>(
    null,
  );
  const [branchNameError, setBranchNameError] = useState<string | null>(null);
  const [ifscError, setIfscError] = useState<string | null>(null);

  const [bankDocument, setBankDocument] = useState<File | null>(null);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);
  const [bankDocumentPreview, setBankDocumentPreview] = useState<string | null>(null);
  const [bankDocumentError, setBankDocumentError] = useState<string | null>(null);

  const deviceType = userDeviceStore((state) => state.deviceType);

  const fetchBankDropdown = async () => {
    const response = await verificationService.retrieveBankDropdown(true);
    if (response.isSuccess) {
      setBankList(response.data);
    }
  };

  useEffect(() => {
    fetchBankDropdown();
  }, []);

  useEffect(() => {
    if (!bankAccount) return;

    setSelectedBank(bankAccount.bankid);
    setBeneficiaryName(bankAccount.beneficiaryname);
    setAccountNumber(bankAccount.accountnumber);
    setBranchName(bankAccount.branchname);
    setIfsc(bankAccount.ifsc);
    
    if (bankAccount.bankstatementimage && bankAccount.status.toLowerCase() !== BankAccountStatus.Rejected.toLowerCase()) {
      const imageUrl = `${BLOB_IMAGE_URL}${BlobContainerConsts.BankAttachment}/${bankAccount.bankstatementimage}`;
      setExistingFileName(bankAccount.bankstatementimage);
      setBankDocumentPreview(imageUrl);
    }
  }, [bankAccount]);

  const validateImage = (file: File) => {
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      return localization.img_extention;
    }
    if (file.size / 1_000_000 > 5) {
      return localization.img_size;
    }
    return null;
  };

  const handleBankDocumentSelect = (file: File | null) => {
    if (!file) return;

    const error = validateImage(file);
    if (error) {
      setBankDocumentError(error);
      setBankDocument(null);
      setBankDocumentPreview(null);
      return;
    }

    setBankDocumentError(null);
    setBankDocument(file);
    setBankDocumentPreview(URL.createObjectURL(file));
  };

  const isFormValid =
    selectedBank &&
    beneficiaryName &&
    accountNumber &&
    branchName &&
    ifsc &&
    (bankDocument || existingFileName) &&
    !bankDocumentError;

  const checkValidation = () => {
    setBeneficiaryNameError("");
    setAccountNumberError("");
    setBranchNameError("");
    setIfscError("");

    let isValid = true;

    if (
      beneficiaryName &&
      !Validations.bankAccount.beneficiaryName.regex.test(beneficiaryName)
    ) {
      setBeneficiaryNameError(localization.valid_beneficiary_name);
      isValid = false;
    }

    if (
      accountNumber &&
      !Validations.bankAccount.accountNumber.regex.test(accountNumber)
    ) {
      setAccountNumberError(localization.bank_account_length);
      isValid = false;
    }

    if (
      branchName &&
      !Validations.bankAccount.branchName.regex.test(branchName)
    ) {
      setBranchNameError(localization.valid_branch_name);
      isValid = false;
    }

    if (ifsc && !Validations.bankAccount.ifsc.regex.test(ifsc)) {
      setIfscError(localization.valid_ifsc_code);
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (!isFormValid && !checkValidation()) return;

    let bankStatementImage = existingFileName;

    if (bankDocument) {
      const uploadRes = await verificationService.uploadBankDocument({
        file: bankDocument,
        oldFileName: existingFileName ?? "",
        fileName: bankDocument.name,
      });

      if (!uploadRes.isSuccess) return;

      bankStatementImage = uploadRes.data?.fileName ?? null;
    }

    const response = await verificationService.persistBankAccount({
      id: bankAccount?.id ?? null,
      bankId: selectedBank!,
      beneficiaryName: beneficiaryName!,
      accountNumber: accountNumber!,
      branchName: branchName!,
      ifsc: ifsc!,
      isPreffered: true,
      bankStatementImage: bankStatementImage!,
    });

    if (response.isSuccess) {
      userPendingStore.getState().fetchPendingData();
      dispatch(increaseInboxUnreadCount());
      toast.success(
        bankAccount
          ? localization.bank_account_updated
          : localization.bank_account_created
      );
      handleBackButton();
    }
  };

  const handleBeneficiaryName = (value: string) => {
    setBeneficiaryName(value);

    const regex = Validations.bankAccount.beneficiaryName.regex;
    const isValid = regex.test(value);
    setBeneficiaryNameError(
      isValid ? null : localization.valid_beneficiary_name,
    );
  };

  const handleAccountNumber = (value: string) => {
    setAccountNumber(value);

    const regex = Validations.bankAccount.accountNumber.regex;
    const isValid = regex.test(value);
    setAccountNumberError(isValid ? null : localization.bank_account_length);
  };

  const handleBranchName = (value: string) => {
    setBranchName(value);

    const regex = Validations.bankAccount.branchName.regex;
    const isValid = regex.test(value);
    setBranchNameError(isValid ? null : localization.valid_branch_name);
  };

  const handleIfscCode = (value: string) => {
    setIfsc(value);

    const regex = Validations.bankAccount.ifsc.regex;
    const isValid = regex.test(value);
    setIfscError(isValid ? null : localization.valid_ifsc_code);
  };

  const content = (
    <div>
      {deviceType !== DeviceType.Web ? (<div className="flex items-center gap-4">
        <Button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#000000] border border-[#1F1F1F] text-white shadow-md"
          onClick={handleBackButton}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
        </Button>

        <span className="text-white text-xl font-bold">
          {bankAccount ? localization.edit_bank : localization.add_bank}
        </span>
      </div>
      ) : (<Header4 className="text-white text-center mb-6">{bankAccount ? localization.edit_bank : localization.add_bank}</Header4>)}

      <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-6">
        <Dropdown
          label={localization.bank_name}
          isRequired
          options={
            bankList?.details.map((bank) => ({
              label:
                language === SupportedLanguages.Hindi
                  ? bank.hindiName
                  : bank.name,
              value: bank.id,
            })) ?? []
          }
          value={selectedBank ?? undefined}
          onChange={setSelectedBank}
        />

        <TextInput
          label={localization.beneficiary_name1}
          isRequired
          placeholder={localization.placeholder_beneficiary_name}
          value={beneficiaryName ?? ""}
          onChange={(e) =>
            setBeneficiaryName(e.target.value.replace(/[^a-zA-Z ]/g, ""))
          }
          onInput={(e) => handleBeneficiaryName(e.currentTarget.value)}
          error={beneficiaryNameError ?? undefined}
        />

        <TextInput
          label={localization.account_number1}
          className="mt-4"
          isRequired={true}
          placeholder={localization.placeholder_account_number}
          value={accountNumber ?? ""}
          onChange={(e) =>
            setAccountNumber(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))
          }
          onInput={(e) => handleAccountNumber(e.currentTarget.value)}
          minLength={9}
          maxLength={18}
          error={accountNumberError ?? undefined}
        />

        <TextInput
          label={localization.branch_name1}
          className="mt-4"
          isRequired
          placeholder={localization.placeholder_branch_name}
          value={branchName ?? ""}
          onChange={(e) =>
            setBranchName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))
          }
          onInput={(e) => handleBranchName(e.currentTarget.value)}
          error={branchNameError ?? undefined}
        />

        <TextInput
          label={localization.swift_ifsc}
          className="mt-4"
          isRequired
          placeholder={localization.placeholder_ifsc}
          value={ifsc ?? ""}
          onChange={(e) => setIfsc(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
          onInput={(e) => handleIfscCode(e.currentTarget.value)}
          error={ifscError ?? undefined}
          maxLength={Validations.bankAccount.ifsc.maxLength ?? 11}
        />
      </div>

      <div className="mt-4 md:grid md:grid-cols-2 md:gap-6">
        <FileUpload
          className="mt-4"
          label={localization.upload_bank_document}
          borderClassName="border border-solid border-[#333333]"
          canPreview={true}
          preview={bankDocumentPreview}
          previewHeight={300}
          helperText={`${localization.upload_image_lbl} ${localization.upload_image_lbl_size}`}
          error={bankDocumentError ?? undefined}
          onSelect={handleBankDocumentSelect}
          onRemove={() => {
            setBankDocument(null);
            setBankDocumentPreview(null);
          }}
        />
      </div>

      <div className="flex justify-center">
        <Button
          type="submit"
          className={`mt-4 md:w-1/4 w-full mx-auto py-4 rounded-md text-white bg-primary-gradient ${!isFormValid
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-primary-gradient/80"
            }`}
          disabled={!isFormValid}
          onClick={handleSubmit}
        >
          <span className="uppercase">{localization.confirm_btn}</span>
        </Button>
      </div>

    </div >
  )

  return (
    deviceType !== DeviceType.Web ? content : (
      <Card>
        <CardHeader showBackButton={true} onBackButtonClick={handleBackButton} contentInline={true} />
        <CardBody showGradient={true}>
          <div className="w-full max-w-4xl mx-auto">
            {content}
          </div>
        </CardBody>
      </Card>
    )

  );
}
